import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ZefForm, ZefFormsManagerService } from '@zerops/zef/forms';
import {
  FormState,
  Boxed,
  box,
  unbox,
  disable,
  setValue,
  enable,
  validate,
  updateArray,
  updateGroup,
  setErrors
} from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';
import { FEATURE_NAME } from './http-routing-form.constant';
import {
  HttpRoutingLocationBasicAuthUser,
  HttpRoutingLocationBasicAuth,
  HttpRoutingLocationRedirect,
  HttpRoutingLocationAccessPolicy,
  HttpRoutingLocationRateLimiting,
  HttpRoutingLocationContent
} from '@zerops/models/http-routing';
import { requiredWhenEnabled } from './http-routing-form.utils';

export interface HttpRoutingFormLocationsConfigValue {
  redirect?: HttpRoutingLocationRedirect;
  basicAuth?: {
    enabled?: boolean;
    realm?: string;
    users: HttpRoutingLocationBasicAuthUser[];
  };
  accessPolicy?: HttpRoutingLocationAccessPolicy;
  rateLimiting?: HttpRoutingLocationRateLimiting;
  content?: HttpRoutingLocationContent;
}

export interface HttpRoutingFormLocationsValue {
    path: string;
    portData: Boxed<{ port: number; serviceStackId: string; }>;
    config: HttpRoutingFormLocationsConfigValue;
  }

export interface HttpRoutingFormValue {
  domains: Boxed<Array<string>>;
  sslEnabled: boolean;
  cdnEnabled: boolean;
  locations: HttpRoutingFormLocationsValue[];
  id: string;
}

export type HttpRoutingFormState = FormState<HttpRoutingFormValue>;

export const HTTP_ROUTING_FORM_LOCATION_DEFAULT_VALUE = {
  path: '',
  portData: box({ port: undefined, serviceStackId: undefined }),
  config: {
    redirect: {
      enabled: false,
      to: '',
      code: undefined,
      preservePath: true,
      preserveQuery: true
    },
    basicAuth: {
      enabled: false,
      realm: '',
      users: [
        {
          password: '',
          username: '',
          comment: ''
        }
      ]
    },
    accessPolicy: {
      enabled: false,
      defaultPolicy: 'allow' as 'allow' | 'deny',
      cidr: []
    },
    rateLimiting: {
      enabled: false,
      rate: undefined,
      burst: undefined,
      zone: '',
      zoneSize: undefined,
      key: 'binary_remote_addr' as 'binary_remote_addr' | 'server_name'
    },
    content: {
      enabled: false,
      code: undefined,
      content: '',
      contentType: ''
    }
  }
};

export const HTTP_ROUTING_FORM_DEFAULT_VALUE = {
  id: undefined,
  domains: box([]),
  sslEnabled: true,
  cdnEnabled: false,
  locations: [ HTTP_ROUTING_FORM_LOCATION_DEFAULT_VALUE ]
};


@Injectable({ providedIn: 'root' })
export class HttpRoutingForm extends ZefForm<HttpRoutingFormValue> {

  constructor(
    public store: Store<any>,
    public formsManager: ZefFormsManagerService
  ) {
    super(
      store,
      formsManager,
      FEATURE_NAME,
      HTTP_ROUTING_FORM_DEFAULT_VALUE,
      {
        domains: (s) => {
          const value = unbox(s.value);
          return value && value.length > 0 
            ? setErrors(s, {})
            : validate(s, required);
        },
        cdnEnabled: (s, ps) => {
          if (ps?.controls?.sslEnabled.value === false) {
            return setValue(disable(s), false);
          } else {
            return enable(s);
          }
        },
        locations: updateArray((locationState) => {
          return updateGroup<HttpRoutingFormLocationsValue>({
            path: validate(required),
            portData: validate(required),
            config: updateGroup<HttpRoutingFormLocationsConfigValue>({
              redirect: updateGroup<HttpRoutingLocationRedirect>({
                to: requiredWhenEnabled<string>,
                code: requiredWhenEnabled<number>,
                preservePath: requiredWhenEnabled<boolean>,
                preserveQuery: requiredWhenEnabled<boolean>
              }),
              basicAuth: updateGroup<HttpRoutingLocationBasicAuth>({
                realm: requiredWhenEnabled<string>,
                users: updateArray(
                  updateGroup<HttpRoutingLocationBasicAuthUser>({
                    username: requiredWhenEnabled(
                      locationState?.controls?.config?.controls?.basicAuth?.controls?.enabled?.value
                    ),
                    password: requiredWhenEnabled(
                      locationState?.controls?.config?.controls?.basicAuth?.controls?.enabled?.value
                    )
                  })
                )
              }),
              accessPolicy: updateGroup<HttpRoutingLocationAccessPolicy>({
                defaultPolicy: requiredWhenEnabled<'allow' | 'deny'>,
                cidr: requiredWhenEnabled
              }),
              rateLimiting: updateGroup<HttpRoutingLocationRateLimiting>({
                rate: requiredWhenEnabled<number>,
                burst: requiredWhenEnabled<number>,
                zone: requiredWhenEnabled<string>,
                key: requiredWhenEnabled<'binary_remote_addr' | 'server_name'>
              }),
              content: updateGroup<HttpRoutingLocationContent>({
                code: requiredWhenEnabled<number>,
                content: requiredWhenEnabled<string>,
                contentType: requiredWhenEnabled<string>
              })
            })
          })(locationState);
        })
      }
    );
  }
}
